import React, { useEffect, useState } from "react";
import { Button, Upload, Row, Col, Alert, Space, Form, Input } from "antd";
import { CloudUploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import styles from "./UploadJustification.module.css";
import { deleteUploadedDocument } from "../../../../apis/patientService";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { UploadDocument } from "../../../../models/EnhancementSlice";
import { message } from "antd";
import FilePreviewModal from "../../PreAuth-query/FilePreviewModal";
import RightSideFilePreview from "./RightSideFilePreview";
import { errorMessage } from "../../../../utils/notificationUtils";

interface IUploadJustificationProps {
  file?: File | null;
  setFile?: (file: File | null) => void;
  onNext?: () => void;
  onCancel?: () => void;
  caseInfo?: {
    caseId?: string;
    patientName?: string;
  };
  uploadedDocuments: {
    documentUrl: string;
    documentType: string;
    documentLabelType: string;
    documentName: string;
    deficiencyCheckStatus: any;
    id: number;
  }[];
  setUploadedDocuments: any;
  handleSubmit: any;
  documentTypes: any;
  activeKey?: any;
  groupDocumentsByType?: any;
  isProcedureSubmitted: boolean;
  sentToQCDocument: boolean;
  isSaving: boolean;
  handleSentToQc: any;
  isIntimating: boolean;
}

const UploadJustification: React.FC<IUploadJustificationProps> = ({
  file,
  setFile,
  onNext,
  onCancel,
  caseInfo,
  uploadedDocuments,
  setUploadedDocuments,
  handleSubmit,
  documentTypes,
  activeKey,
  groupDocumentsByType,
  isProcedureSubmitted,
  sentToQCDocument,
  isSaving,
  handleSentToQc,
  isIntimating
}) => {
  const { patients, auth, enhancement } = useSelector(
    (state: RootState) => state,
  );
  const dispatch = useDispatch<AppDispatch>();
  const [selectedFile, setSelectedFile] = useState<{
    file: File;
    documentType: string;
  } | null>(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });

  const handleFileChange = (documentType: string) => (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }

    if (e.file?.type !== "application/pdf") {
      errorMessage("File Must be PDF only");
      return;
    }

    if (e?.file) {
      setSelectedFile({ file: e.file, documentType: documentType });
      setPreviewFile({
        url: URL.createObjectURL(e.file),
        name: e.file.name,
        showUpload: true,
        documentType: documentType,
      });
      setPreviewModalVisible(true);
    }
    return e?.target?.files;
  };

  const handleUpload = async () => {
    const CollectPostData = new FormData();
    if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
      CollectPostData.append(
        "hospitalId",
        patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.patientResponse?.id) {
      CollectPostData.append(
        "patientId",
        patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      CollectPostData.append(
        "caseId",
        patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      CollectPostData.append(
        "visitId",
        patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
      );
    }
    CollectPostData.append("documentType", previewFile.documentType);
    if (selectedFile?.file) {
      CollectPostData.append("file", selectedFile?.file);
    }
    try {
      const documentRes = await dispatch(
        UploadDocument({
          formData: CollectPostData,
          accessToken: auth.accessToken || "",
        }),
      ).unwrap();

      const updatedDocuments = [...uploadedDocuments, ...documentRes];
      setUploadedDocuments(updatedDocuments);
      setPreviewModalVisible(false);
      message.success(
        `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
      );
    } catch (error) {
      console.error("Failed to upload document:", error);
    }
  };

  const handleDeleteList = async (
    documentType: string,
    documentUrl: string,
    i: number,
  ) => {
    const deleteDocument = uploadedDocuments?.find(
      (doc, index) => doc?.documentUrl === documentUrl,
    );
    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };
    const newUpdatedDocumentList = uploadedDocuments?.filter(
      (doc, index) => doc?.documentUrl !== documentUrl,
    );
    setUploadedDocuments(newUpdatedDocumentList);
    if (deleteDocument?.documentUrl) {
      try {
        await deleteUploadedDocument(deleteForm);
        // message.success("Document Deleted Successfully");
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <div className={styles.uploadJustificationContent}>
      {/* <div className={styles.uploadJustificationHeader}>
        <p className={styles.uploadJustificationSubtitle}>
          {caseInfo?.caseId} • PATIENT: {caseInfo?.patientName}
        </p>
      </div> */}
      <Row gutter={[16, 16]} style={{ height: "90vh", marginBottom: 24 }}>
        {documentTypes
          ?.filter((docType: any) =>
            docType?.label?.toLowerCase()?.includes("justification"),
          )
          ?.map((docType: any) => (
            <Col xs={24} sm={24} md={24}>
              <div className={styles.uploadJustificationSection}>
                <div className={styles.uploadJustificationLabel}>
                  {docType?.label?.toLowerCase()?.includes("justification")
                    ? "Justification Letter"
                    : docType?.label}
                  <span className={styles.uploadJustificationRequired}>*</span>
                  <span className={styles.uploadJustificationBadge}>
                    REQUIRED
                  </span>
                </div>
                <p className={styles.uploadJustificationHint}>
                  Required signature by treating doctor
                </p>

                {groupDocumentsByType(uploadedDocuments)?.[docType?.labelId]
                  ?.length > 0 ? (
                  // && !docType?.multiple
                  <RightSideFilePreview
                    documents={
                      groupDocumentsByType(uploadedDocuments)?.[
                        docType?.labelId
                      ]
                    }
                    handleDeleteList={handleDeleteList}
                    activeKey={activeKey}
                    uploadedDocuments={uploadedDocuments}
                    setUploadedDocuments={setUploadedDocuments}
                    isSubmitted={isProcedureSubmitted || (auth?.role?.role === "FO_STAFF" && sentToQCDocument)}
                  />
                ) : (
                  <div className={styles.uploadJustificationBox}>
                    <Upload
                      multiple={false}
                      showUploadList={false}
                      onChange={handleFileChange(docType?.labelId)}
                      beforeUpload={() => false}
                      className={styles.uploadJustificationUpload}
                    >
                      <div className={styles.uploadJustificationContent}>
                        <CloudUploadOutlined
                          className={styles.uploadJustificationIcon}
                        />
                        <p className={styles.uploadJustificationText}>
                          Drop{" "}
                          {docType?.label
                            ?.toLowerCase()
                            ?.includes("justification")
                            ? "Justification Letter"
                            : docType?.label}
                        </p>
                        {/* <p className={styles.uploadJustificationUploadHint}>Max 20MB • PDF only</p> */}
                        <Button
                          type="primary"
                          style={{
                            marginTop: 16,
                            background: "#00549E",
                            color: "white",
                          }}
                        >
                          Browse
                        </Button>
                      </div>
                    </Upload>
                  </div>
                )}

                {/* {fileList.length > 0 && (
              <div className={styles.uploadJustificationFileInfo}>
                <p className={styles.uploadJustificationFileName}>{fileList[0]?.name}</p>
              </div>
            )} */}
              </div>
            </Col>
          ))}

        {/* <Col xs={24} sm={24} md={10}>
          <div className={styles.uploadJustificationInfoSection} style={{display: "flex", justifyContent: "center", alignItems: "center", height: "100%"}}>
            <div className={styles.uploadJustificationInfoBox}>
              <div style={{display: "flex", alignItems: "center", gap: 8}}>
              <InfoCircleOutlined
                className={styles.uploadJustificationInfoIcon}
              />
              <p className={styles.uploadJustificationInfoTitle}>Guidelines</p>
              </div>
              <ul className={styles.uploadJustificationInfoList}>
                <li style={{color: "#00549E"}}>
                  Ensure the justification letter clearly outlines the medical
                  necessity of the procedure and patient identifiers match the
                  records in the portal.
                </li>
                <li style={{color: "#00549E"}}>
                  Missing or illegible signatures may delay the approval
                  process.
                </li>
                <li style={{color: "#00549E"}}>
                  Document should be uploaded in High Resolution PDF only.
                </li>
              </ul>
            </div>
          </div>
        </Col> */}
      </Row>

      <div className={styles.uploadJustificationActionButtons} style={{marginTop: 24}}>
        <Space>
          {onCancel && (
            <Button size="large" onClick={onCancel}>
              Back to History
            </Button>
          )}

          {uploadedDocuments?.some((doc: any) =>
            doc?.documentLabelType?.toLowerCase()?.includes("justification"),
          ) && (
            <Button
              size="large"
              onClick={onNext}
              style={{ background: "#00549e", color: "#fff" }}
            >
              Next
            </Button>
          )}

          {auth?.role?.role === "FO_STAFF" && sentToQCDocument ? null : !isProcedureSubmitted && (
            <Button
              type="primary"
              size="large"
              loading={isSaving}
              // disabled={!file || fileList.length === 0}
              onClick={() => handleSubmit(false)}
              style={{ background: "#F1F5F9", color: "#00549e" }}
            >
              {/* auth?.role?.role === "FO_STAFF" ? "Sent to QC" :  */}
              {"Save As Draft"}
            </Button>
          )}

          { auth?.role?.role === "FO_STAFF" && !sentToQCDocument && !isProcedureSubmitted && (
            <Button
              type="primary"
              size="large"
              loading={isIntimating}
              // disabled={!file || fileList.length === 0}
              onClick={handleSentToQc}
              style={{ backgroundColor: "#00549e", color: "#F1F5F9" }}
            >
              {/* auth?.role?.role === "FO_STAFF" ? "Sent to QC" :  */}
              {"Sent To QC"}
            </Button>
          )}
        </Space>
      </div>

      <FilePreviewModal
        isVisible={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        fileUrl={previewFile.url}
        fileName={previewFile.name}
        documentType={previewFile.documentType}
        handleUpload={handleUpload}
        showUploadButton={previewFile.showUpload}
        key={selectedFile?.file?.lastModified || "no-file"}
        isLoading={enhancement?.isLoading}
      />
    </div>
  );
};

export default UploadJustification;
